import { Phone, MapPin, ShoppingCart, CreditCard, Navigation, Clock } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import CustomerAvatar from './CustomerAvatar';
import CustomerStatusBadge from './CustomerStatusBadge';
import { cur } from '@/lib/currency';

function fmtAmount(n) {
  if (!n) return '0';
  return Number(n).toLocaleString('ar-EG', { maximumFractionDigits: 0 });
}

function lastOrderText(days) {
  if (days === null || days === undefined) return null;
  if (days === 0) return 'طلب اليوم';
  if (days === 1) return 'منذ أمس';
  if (days < 7) return `منذ ${days} أيام`;
  if (days < 30) return `منذ ${Math.floor(days / 7)} أسبوع`;
  if (days < 60) return 'منذ شهر';
  return `منذ ${Math.floor(days / 30)} شهر`;
}

export default function CustomerCard({ customer, onCall }) {
  const navigate = useNavigate();
  const phone = customer.phone || customer.mobile;
  const debt = customer.debit || 0;
  const credit = customer.credit || 0;

  return (
    <div
      className="bg-card border border-border rounded-2xl p-4 flex gap-3 cursor-pointer hover:shadow-md hover:border-primary/30 transition-all active:scale-[0.99]"
      onClick={() => navigate(`/customers/${customer.id}`)}
    >
      <CustomerAvatar name={customer.name} size="md" />

      <div className="flex-1 min-w-0">
        {/* Row 1: Name + Status */}
        <div className="flex items-start justify-between gap-2">
          <p className="font-bold text-foreground leading-snug line-clamp-1 flex-1">{customer.name}</p>
          <CustomerStatusBadge daysOverdue={debt > 0 ? 1 : 0} daysSinceOrder={customer.days_since_order} />
        </div>

        {/* Row 2: Phone + City */}
        <div className="flex items-center gap-3 mt-1 flex-wrap">
          {phone && (
            <button
              onClick={e => { e.stopPropagation(); onCall?.(phone); window.location.href = `tel:${phone}`; }}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
            >
              <Phone className="w-3 h-3" />
              <span dir="ltr">{phone}</span>
            </button>
          )}
          {customer.city && (
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <MapPin className="w-3 h-3" />
              {customer.city}
            </span>
          )}
        </div>

        {/* Row 3: Last order + balance */}
        <div className="flex items-center gap-3 mt-1.5 flex-wrap">
          {customer.days_since_order !== null && customer.days_since_order !== undefined && (
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Clock className="w-3 h-3" />
              {lastOrderText(customer.days_since_order)}
            </span>
          )}
          {debt > 0 && (
            <span className="text-xs font-semibold text-red-600 bg-red-50 px-1.5 py-0.5 rounded-lg">
              متأخر: {fmtAmount(debt)} {cur()}
            </span>
          )}
          {credit > 0 && !debt && (
            <span className="text-xs text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-lg">
              رصيد: {fmtAmount(credit)} {cur()}
            </span>
          )}
        </div>
      </div>

      {/* Quick actions */}
      <div className="flex flex-col gap-1.5 flex-shrink-0">
        <button
          onClick={e => { e.stopPropagation(); navigate(`/orders/new?customer_id=${customer.id}&customer_name=${encodeURIComponent(customer.name)}`); }}
          className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center hover:bg-primary/20 transition-colors"
          title="طلب جديد"
        >
          <ShoppingCart className="w-3.5 h-3.5 text-primary" />
        </button>
        <button
          onClick={e => { e.stopPropagation(); navigate(`/collections/new?customer_id=${customer.id}&customer_name=${encodeURIComponent(customer.name)}`); }}
          className="w-8 h-8 rounded-xl bg-secondary/10 flex items-center justify-center hover:bg-secondary/20 transition-colors"
          title="تحصيل"
        >
          <CreditCard className="w-3.5 h-3.5 text-secondary" />
        </button>
      </div>
    </div>
  );
}